import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  addDoc, 
  serverTimestamp,
  arrayUnion,
  getDocs
} from 'firebase/firestore';

// Sample data for collections
const sampleUsers = [
  {
    email: 'client1@example.com',
    displayName: 'John Doe',
    role: 'client',
    phone: '+919876543210',
    city: 'Mumbai',
    profileComplete: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  },
  {
    email: 'client2@example.com',
    displayName: 'Jane Smith',
    role: 'client',
    phone: '+919876543211',
    city: 'Delhi',
    profileComplete: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }
];

const sampleLawyers = [
  {
    email: 'lawyer1@example.com',
    displayName: 'Adv. Rajesh Kumar',
    role: 'lawyer',
    phone: '+919876543212',
    location: 'Mumbai',
    specializations: ['criminal', 'civil'],
    experience: 8,
    consultationFee: 800,
    rating: 4.5,
    totalReviews: 25,
    isVerified: true,
    availableForConsultation: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  },
  {
    email: 'lawyer2@example.com',
    displayName: 'Adv. Priya Sharma',
    role: 'lawyer',
    phone: '+919876543213',
    location: 'Delhi',
    specializations: ['family', 'property'],
    experience: 12,
    consultationFee: 1200,
    rating: 4.8,
    totalReviews: 42,
    isVerified: true,
    availableForConsultation: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }
];

const sampleAppointments = [
  {
    clientId: 'client1',
    lawyerId: 'lawyer1',
    date: new Date(Date.now() + 24 * 60 * 60 * 1000), // Tomorrow
    duration: 60,
    status: 'confirmed',
    type: 'video',
    amount: 800,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  },
  {
    clientId: 'client2',
    lawyerId: 'lawyer2',
    date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000), // Day after tomorrow
    duration: 45,
    status: 'pending',
    type: 'audio',
    amount: 1200,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }
];

const sampleChats = [
  {
    participants: ['client1', 'lawyer1'],
    lastMessage: 'Thank you for the consultation',
    lastMessageTime: serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  },
  {
    participants: ['client2', 'lawyer2'],
    lastMessage: 'When can we schedule the next meeting?',
    lastMessageTime: serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }
];

const sampleSubscriptions = [
  {
    userId: 'client1',
    planId: 'premium',
    status: 'active',
    startDate: new Date(),
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days
    amount: 999,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }
];

// Function to setup all collections
export const setupFirestoreCollections = async () => {
  try {
    console.log('🚀 Setting up Firestore collections...');

    // 1. Setup Users Collection
    console.log('📝 Creating users collection...');
    for (const userData of sampleUsers) {
      const userRef = await addDoc(collection(db, 'users'), userData);
      console.log(`✅ Created user: ${userRef.id}`);
    }

    // 2. Setup Lawyers Collection
    console.log('👨‍💼 Creating lawyers collection...');
    for (const lawyerData of sampleLawyers) {
      const lawyerRef = await addDoc(collection(db, 'lawyers'), lawyerData);
      console.log(`✅ Created lawyer: ${lawyerRef.id}`);
    }

    // 3. Setup Appointments Collection
    console.log('📅 Creating appointments collection...');
    for (const appointmentData of sampleAppointments) {
      const appointmentRef = await addDoc(collection(db, 'appointments'), appointmentData);
      console.log(`✅ Created appointment: ${appointmentRef.id}`);
    }

    // 4. Setup Chats Collection
    console.log('💬 Creating chats collection...');
    for (const chatData of sampleChats) {
      const chatRef = await addDoc(collection(db, 'chats'), chatData);
      console.log(`✅ Created chat: ${chatRef.id}`);
    }

    // 5. Setup Subscriptions Collection
    console.log('💳 Creating subscriptions collection...');
    for (const subscriptionData of sampleSubscriptions) {
      const subscriptionRef = await addDoc(collection(db, 'subscriptions'), subscriptionData);
      console.log(`✅ Created subscription: ${subscriptionRef.id}`);
    }

    console.log('🎉 All collections created successfully!');
    return true;

  } catch (error) {
    console.error('❌ Error setting up collections:', error);
    return false;
  }
};

// Function to create a single collection with sample data
export const createCollection = async (collectionName, sampleData) => {
  try {
    console.log(`📝 Creating ${collectionName} collection...`);
    
    for (const data of sampleData) {
      const docRef = await addDoc(collection(db, collectionName), data);
      console.log(`✅ Created ${collectionName} document: ${docRef.id}`);
    }
    
    return true;
  } catch (error) {
    console.error(`❌ Error creating ${collectionName} collection:`, error);
    return false;
  }
};

// Function to check if collections exist
export const checkCollectionsExist = async () => {
  const collections = ['users', 'lawyers', 'appointments', 'chats', 'subscriptions'];
  const results = {};
  
  for (const collectionName of collections) {
    try {
      const querySnapshot = await getDocs(collection(db, collectionName));
      results[collectionName] = querySnapshot.size > 0;
    } catch (error) {
      results[collectionName] = false;
    }
  }
  
  return results;
};

export default setupFirestoreCollections;
